import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { isConflictError, getApiErrorMessage } from "@/lib/api/api";
import { X, Search, Plus, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { OVERLAY_SOFT } from "@/components/ui/overlay";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useAddIngredient, useUpdateIngredient, useRecipes } from "@/hooks/recipes";
import { useMaterials } from "@/hooks/useMaterials";
import type { Material } from "@/types/material";
import { ingredientLineName, type Recipe, type RecipeIngredient, type SubRecipeRef } from "@/types/recipe";
import { CreateMaterialDialog } from "./CreateMaterialDialog";
import { UnitSelect } from "@/components/units/UnitSelect";
import { useUnits } from "@/hooks/useUnits";
import { UnitConversionWarning } from "@/components/units/UnitConversionWarning";
import { recipeUnitFor, subRecipeUnitFor, unitMayNotConvert } from "@/lib/units/unit-options";

// ── Shared types ──────────────────────────────────────────────────────────────

/**
 * What a line names. A line is one or the other, never both — the same rule the
 * API enforces, so the form cannot compose a request the server will reject.
 */
type IngredientSource = "MATERIAL" | "RECIPE";

interface IngredientFormState {
  source: IngredientSource;
  search: string;
  selectedMaterial: Material | null;
  selectedRecipe: SubRecipeRef | null;
  quantity: string;
  unit: string;
  groupName: string;
  prepNotes: string;
  isSubRecipe: boolean;
}

const EMPTY_FORM: IngredientFormState = {
  source: "MATERIAL",
  search: "",
  selectedMaterial: null,
  selectedRecipe: null,
  quantity: "",
  unit: "",
  groupName: "",
  prepNotes: "",
  isSubRecipe: false,
};

const COMMON_ALLERGENS = [
  "Gluten", "Egg", "Dairy", "Soy", "Peanut",
  "Tree Nut", "Fish", "Shellfish", "Sesame", "Wheat", "Mustard", "Celery",
] as const;

// ── Shared modal shell ────────────────────────────────────────────────────────

function IngredientDialogShell({
  open,
  onClose,
  title,
  recipeId,
  form,
  setForm,
  sourceLocked,
  autoFocusSearch,
  isSaving,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The recipe being edited, so it cannot be offered as its own ingredient. */
  recipeId: number;
  form: IngredientFormState;
  setForm: React.Dispatch<React.SetStateAction<IngredientFormState>>;
  /** Editing changes a line's amount, not what it names. */
  sourceLocked?: boolean;
  autoFocusSearch?: boolean;
  isSaving: boolean;
  onSave: () => void;
}) {
  const { t } = useLingui();
  const [showDropdown, setShowDropdown] = useState(false);
  const [createMaterialOpen, setCreateMaterialOpen] = useState(false);
  const [prepDishStation, setPrepDishStation] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isRecipeSource = form.source === "RECIPE";
  const hasSearch = form.search.length >= 1;

  const { data: materialsPage } = useMaterials(form.search, !isRecipeSource && hasSearch);
  const materials = materialsPage?.data ?? [];

  const { data: recipesPage } = useRecipes(
    { search: form.search, limit: 20 },
    isRecipeSource && hasSearch,
  );
  // A recipe cannot be an ingredient of itself; the API returns 422 for it, and
  // there is no reason to offer it and then explain the refusal.
  const recipeResults = (recipesPage?.data ?? []).filter((r) => r.id !== recipeId);

  const hasSelection = isRecipeSource ? form.selectedRecipe !== null : form.selectedMaterial !== null;
  const results: Array<Material | Recipe> = isRecipeSource ? recipeResults : materials;
  const selectedId = (isRecipeSource ? form.selectedRecipe?.id : form.selectedMaterial?.id) ?? null;

  // Every unit, whichever family the ingredient is in: a line is allowed to be
  // measured any way the cook likes. Units costing cannot convert are warned
  // about below the picker instead of being hidden from it.
  const { groups: unitGroups } = useUnits(open, "all");
  // A case is bought, not measured: the API refuses a line in one, so the
  // container family is not offered (a value already on the line still shows).
  const measureGroups = useMemo(
    () => unitGroups.filter((group) => group.family !== "container"),
    [unitGroups],
  );

  const pricedUnit = isRecipeSource
    ? form.selectedRecipe
      ? subRecipeUnitFor(form.selectedRecipe)
      : null
    : (form.selectedMaterial?.effectiveUnitCost?.unit ?? null);
  const unitWarning =
    hasSelection &&
    form.unit.trim() !== "" &&
    pricedUnit !== null &&
    unitMayNotConvert(unitGroups, form.unit, pricedUnit, {
      densityGPerMl: form.selectedMaterial?.densityGPerMl,
      pack: form.selectedMaterial
        ? {
            purchaseUom: form.selectedMaterial.unitOfMeasure,
            packQuantity: form.selectedMaterial.packQuantity,
            packUom: form.selectedMaterial.packUom ?? "",
          }
        : null,
    })
      ? pricedUnit
      : null;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open || !autoFocusSearch) {
      return;
    }
    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open, autoFocusSearch]);

  /**
   * Picking an ingredient loads the unit that ingredient is measured in, and
   * the cook overrides it from there if this line calls for another. Carrying
   * the previous ingredient's unit over instead — which is what this used to do
   * whenever the two happened to share a family — meant the box could disagree
   * with the ingredient that had just been chosen. Whatever is showing at save
   * is what the line stores; nothing is written back to the ingredient.
   */
  function handleSelectMaterial(material: Material) {
    setForm((f) => ({
      ...f,
      selectedMaterial: material,
      selectedRecipe: null,
      search: material.name,
      unit: recipeUnitFor(material),
    }));
    setShowDropdown(false);
  }

  function handleSelectRecipe(recipe: Recipe) {
    const ref: SubRecipeRef = {
      id: recipe.id,
      name: recipe.name,
      recipeType: recipe.recipeType,
      servings: recipe.servings ?? null,
      yieldQuantity: recipe.yieldQuantity ?? null,
      yieldUom: recipe.yieldUom ?? null,
    };
    // A recipe with no yield has no unit to be measured in. The line is still
    // allowed — the API costs it as unavailable and says why — so the unit is
    // simply left for the cook to choose.
    setForm((f) => ({
      ...f,
      selectedRecipe: ref,
      selectedMaterial: null,
      search: recipe.name,
      // Keep this blank so the picker opens from the top of the full catalog.
      // Pre-selecting "each" hid weight/volume choices behind scroll position.
      unit: "",
    }));
    setShowDropdown(false);
  }

  function handleChangeSource(next: IngredientSource) {
    // What was picked, the unit it implied, and the text that found it all
    // belong to the old kind of line.
    setForm((f) => ({
      ...f,
      source: next,
      search: "",
      selectedMaterial: null,
      selectedRecipe: null,
      unit: "",
    }));
    setShowDropdown(false);
  }

  const allergens = form.selectedMaterial?.allergens ?? [];
  const allergenCodes = new Set(allergens.map((a) => a.allergen.name.toLowerCase()));

  const parsedQuantity = parseQuantityInput(form.quantity);
  const extCost =
    form.selectedMaterial?.unitCost != null && Number.isFinite(parsedQuantity) && parsedQuantity > 0
      ? (Number(form.selectedMaterial.unitCost) * parsedQuantity).toFixed(2)
      : null;

  const sectionDescription = t`Fill in the fields below. Values are saved when you click "Save ingredient".`;

  return (
    <>
    <CreateMaterialDialog
      open={createMaterialOpen}
      onOpenChange={setCreateMaterialOpen}
      onCreated={(name) => {
        setForm((f) => ({ ...f, search: name, selectedMaterial: null }));
        setShowDropdown(true);
      }}
    />
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        hideClose
        overlayClassName={OVERLAY_SOFT}
        className="w-[56.25rem] max-w-[96vw] gap-0 overflow-hidden rounded-xl border-(--color-border) bg-(--color-surface-content) p-0 shadow-xl"
      >
          <div className="flex items-start justify-between border-b border-(--color-border) px-5 py-5">
            <div className="flex flex-col gap-2">
              <DialogTitle className="text-[2.125rem] leading-[1.1] font-medium tracking-normal text-(--color-text-primary)">{title}</DialogTitle>
              <DialogDescription className="text-base text-(--color-text-secondary)">{t`Search for an ingredient, set quantity and unit, then save.`}</DialogDescription>
            </div>
            <Button
              variant="ghost"
              type="button"
              onClick={onClose}
              className="mt-0.5 h-7 w-7 p-0 text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
            >
              <X className="size-5" />
            </Button>
          </div>

          <div className="max-h-[68vh] overflow-y-auto">
            <SectionRow title={t`Ingredient details`} description={sectionDescription}>
              <div className="flex flex-col gap-3">
                <FormField label={t`This line is`}>
                  {/* A plain select: what a line names decides which search runs
                      and which id is sent, so it is a choice, not a toggle. */}
                  <select
                    aria-label={t`This line is`}
                    value={form.source}
                    disabled={sourceLocked}
                    onChange={(e) => handleChangeSource(e.target.value as IngredientSource)}
                    className="h-10 w-full rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline-none disabled:bg-(--color-surface-muted) disabled:text-(--color-text-secondary)"
                  >
                    <option value="MATERIAL">{t`An ingredient`}</option>
                    <option value="RECIPE">{t`Another recipe`}</option>
                  </select>
                </FormField>

                <div className="grid grid-cols-2 gap-3">
                <FormField label={isRecipeSource ? t`Recipe name` : t`Ingredient name`}>
                  <div className="relative" ref={dropdownRef}>
                    <div className="flex h-10 items-center gap-2 rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 shadow-[0px_1px_2px_rgba(16,24,40,0.05)]">
                      <Search className="size-4 shrink-0 text-(--color-text-secondary)" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={form.search}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            search: e.target.value,
                            selectedMaterial: null,
                            selectedRecipe: null,
                          }));
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        placeholder={
                          isRecipeSource ? t`Enter recipe name...` : t`Enter ingredient name...`
                        }
                        className="min-w-0 flex-1 bg-transparent text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                      />
                      {hasSelection && (
                        <Button
                          variant="ghost"
                          type="button"
                          aria-label={t`Clear selection`}
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              selectedMaterial: null,
                              selectedRecipe: null,
                              search: "",
                            }));
                            setShowDropdown(false);
                          }}
                          className="h-auto p-0 text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
                        >
                          <X className="size-3.5" />
                        </Button>
                      )}
                    </div>

                    {showDropdown && hasSearch && (
                      <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-(--color-border) bg-(--color-surface-content) shadow-lg">
                        {results.length > 0 && (
                          <div className="border-b border-(--color-border) px-3 py-1.5">
                            <span className="text-xs text-(--color-text-secondary)">
                              {t`Results: ${results.length}`}
                            </span>
                          </div>
                        )}
                        <div className="max-h-44 overflow-y-auto">
                          {results.length === 0 ? (
                            <p className="px-3 py-3 text-sm text-(--color-text-secondary)">
                              {isRecipeSource ? t`No recipes found` : t`No ingredients found`}
                            </p>
                          ) : (
                            results.map((result) => (
                              <Button
                                key={result.id}
                                variant="ghost"
                                type="button"
                                onClick={() =>
                                  isRecipeSource
                                    ? handleSelectRecipe(result as Recipe)
                                    : handleSelectMaterial(result as Material)
                                }
                                className="h-auto w-full justify-start rounded-none px-3 py-2.5 text-left hover:bg-(--color-surface-muted)"
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-(--color-surface-accent)">
                                    <span className="text-xs font-semibold text-(--color-text-secondary)">
                                      {result.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <p className="truncate text-sm text-(--color-text-primary)">{result.name}</p>
                                </div>
                              </Button>
                            ))
                          )}
                        </div>
                        {!isRecipeSource && (
                          <div className="border-t border-(--color-border)">
                            <Button
                              variant="ghost"
                              type="button"
                              onClick={() => {
                                setShowDropdown(false);
                                setCreateMaterialOpen(true);
                              }}
                              className="h-auto w-full justify-start gap-2 rounded-none px-3 py-2.5 text-sm font-medium text-(--color-text-primary) hover:bg-(--color-surface-muted)"
                            >
                              <Plus className="size-4" />
                              {t`Add new ingredient`}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </FormField>

                <FormField label={isRecipeSource ? t`Recipe ID` : t`Ingredient ID`}>
                  <div className="flex h-10 items-center rounded-[8px] border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-text-secondary) shadow-[0px_1px_2px_rgba(16,24,40,0.05)]">
                    {selectedId === null ? t`Data will be filled automatically` : `#${selectedId}`}
                  </div>
                </FormField>
                </div>
              </div>
            </SectionRow>

            <SectionRow title={t`Ingredient proportions`} description={sectionDescription}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label={t`Quantity`} required>
                  <input
                    id="quantity"
                    name="quantity"
                    type="text"
                    inputMode="decimal"
                    value={form.quantity}
                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                    placeholder={t`E.g. 200`}
                    className="h-10 w-full rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline-none placeholder:text-(--color-text-secondary)"
                  />
                </FormField>
                <FormField label={t`Unit of Measure`} required>
                  <UnitSelect
                    label={t`Unit of Measure`}
                    value={form.unit}
                    onChange={(value) => setForm((f) => ({ ...f, unit: value }))}
                    groups={measureGroups}
                    disabled={!hasSelection}
                    placeholder={
                      !hasSelection
                        ? isRecipeSource
                          ? t`Choose a recipe first`
                          : t`Choose an ingredient first`
                        : t`Select a unit...`
                    }
                    className="rounded-[8px] shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
                  />
                  {unitWarning !== null && (
                    <UnitConversionWarning
                      unit={form.unit}
                      pricedUnit={unitWarning}
                      isRecipe={isRecipeSource}
                    />
                  )}
                </FormField>
              </div>
            </SectionRow>

            <SectionRow title={t`Allergens`} description={sectionDescription}>
              {isRecipeSource ? (
                <p className="text-sm text-(--color-text-secondary)">
                  {t`Allergens come from what that recipe is made of, and follow it here on their own.`}
                </p>
              ) : (
              <div className="flex flex-wrap gap-3">
                {COMMON_ALLERGENS.map((name) => {
                  const active = allergenCodes.has(name.toLowerCase());
                  return (
                    <button
                      key={name}
                      type="button"
                      className="flex h-9 items-center gap-1.5 rounded-[10px] bg-(--color-surface-muted) px-2"
                      aria-pressed={active}
                    >
                      <span className={`size-5 rounded-full border ${active ? "border-(--color-action) bg-(--color-action)" : "border-(--color-border) bg-(--color-surface-content)"}`} />
                      <span className="text-sm font-medium text-(--color-text-primary)">{name}</span>
                    </button>
                  );
                })}
                <Button
                  variant="ghost"
                  type="button"
                  className="h-9 rounded-[10px] border border-dashed border-(--color-border) bg-transparent px-2 text-sm text-(--color-text-primary) hover:bg-(--color-surface-muted)"
                >
                  <Plus className="size-4" />
                  {t`Add custom allergen`}
                </Button>
              </div>
              )}
            </SectionRow>

            <SectionRow title={t`More details`} description={sectionDescription}>
              <div className="flex flex-col gap-3">
                <FormField label={t`Prep dish / station`} optional>
                  <input
                    type="text"
                    value={prepDishStation}
                    onChange={(e) => setPrepDishStation(e.target.value)}
                    placeholder={t`Select`}
                    className="h-10 w-full rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline-none placeholder:text-(--color-text-secondary)"
                  />
                </FormField>

                <FormField label={t`Group name`} optional>
                  <input
                    type="text"
                    value={form.groupName}
                    onChange={(e) => setForm((f) => ({ ...f, groupName: e.target.value }))}
                    placeholder={t`E.g. Dough`}
                    className="h-10 w-full rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline-none placeholder:text-(--color-text-secondary)"
                  />
                </FormField>

                <FormField label={t`Prep notes`} optional>
                  <textarea
                    value={form.prepNotes}
                    onChange={(e) => setForm((f) => ({ ...f, prepNotes: e.target.value }))}
                    rows={4}
                    placeholder={t`Select`}
                    className="w-full resize-none rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                  />
                </FormField>

                <FormField label={t`Image`} optional>
                  <div className="flex gap-3">
                    <div className="flex h-[56px] w-[56px] items-center justify-center rounded-[10px] border border-dashed border-(--color-border)">
                      <ImagePlus className="size-4 text-(--color-text-secondary)" />
                    </div>
                    <div className="flex h-[104px] flex-1 flex-col items-center justify-center rounded-[10px] border border-(--color-border) bg-(--color-surface-content) text-center text-(--color-text-secondary)">
                      <ImagePlus className="mb-2 size-5" />
                      <p className="text-sm">{t`Click to upload or drag and drop`}</p>
                      <p className="text-xs">{t`SVG, PNG, JPG or GIF (max. 800x400px)`}</p>
                    </div>
                  </div>
                </FormField>
              </div>
            </SectionRow>

            <SectionRow title={t`Cost calculations`} description={sectionDescription}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label={t`Unit cost (inherited)`}>
                  <div className="flex h-10 items-center rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-secondary)">
                    {form.selectedMaterial?.unitCost != null
                      ? `$${Number(form.selectedMaterial.unitCost).toFixed(4)}`
                      : "—"}
                  </div>
                </FormField>
                <FormField label={t`Ext. Cost (calculated)`}>
                  <div className="flex h-10 items-center rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-secondary)">
                    {extCost != null ? `$${extCost}` : "—"}
                  </div>
                </FormField>
              </div>
            </SectionRow>

            <SectionRow title={t`Sub-recipe ingredients`} description={sectionDescription}>
              <div className="flex items-start justify-between rounded-[10px] bg-(--color-surface-content)">
                <div className="flex items-start gap-3">
                  <Switch
                    checked={form.isSubRecipe}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, isSubRecipe: v }))}
                  />
                  <div className="pt-0.5">
                    <p className="text-sm font-medium text-(--color-text-primary)">{t`Sub-recipe ingredients`}</p>
                    <p className="text-sm text-(--color-text-secondary)">{t`This ingredient contains other ingredients.`}</p>
                  </div>
                </div>
              </div>
            </SectionRow>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-(--color-border) px-5 py-4">
            <Button variant="brand-outline" type="button" onClick={onClose} className="h-11 w-[168px]">
              {t`Cancel`}
            </Button>
            <Button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="h-11 w-[168px] rounded-[10px] bg-(--color-action) text-sm font-medium text-(--color-action-fg) hover:bg-(--color-action)/90 disabled:opacity-50"
            >
              {isSaving ? t`Saving...` : t`Save ingredient`}
            </Button>
          </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ── AddIngredientDialog ────────────────────────────────────────────────────────

interface AddIngredientDialogProps {
  recipeId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddIngredientDialog({ recipeId, open, onOpenChange }: AddIngredientDialogProps) {
  const { t } = useLingui();
  const addIngredient = useAddIngredient();
  const [form, setForm] = useState<IngredientFormState>(EMPTY_FORM);

  function handleClose() {
    setForm(EMPTY_FORM);
    onOpenChange(false);
  }

  function handleSave() {
    const isRecipeSource = form.source === "RECIPE";
    const selected = isRecipeSource ? form.selectedRecipe : form.selectedMaterial;
    if (!selected) {
      toast.error(isRecipeSource ? t`Please select a recipe` : t`Please select an ingredient`);
      return;
    }
    const qty = parseQuantityInput(form.quantity);
    if (!form.quantity || !Number.isFinite(qty) || qty <= 0) {
      toast.error(t`Please enter a valid quantity`);
      return;
    }
    if (!form.unit.trim()) {
      toast.error(t`Please select a unit of measure`);
      return;
    }

    addIngredient.mutate(
      {
        id: recipeId,
        data: {
          // One or the other, never both — a line names an ingredient or a
          // recipe, and the API refuses anything else.
          ...(isRecipeSource ? { subRecipeId: selected.id } : { materialId: selected.id }),
          quantity: qty,
          unit: form.unit,
          // Sent even when blank: an emptied box is a value being cleared, and
          // dropping it left the old note on the line.
          preparationNotes: form.prepNotes.trim(),
          groupName: form.groupName.trim(),
        },
      },
      {
        onSuccess: () => {
          toast.success(t`Ingredient added`);
          handleClose();
        },
        onError: (error) => {
          const name = selected.name;
          const message = isConflictError(error)
            ? t`"${name}" is already an ingredient in this recipe`
            : getApiErrorMessage(error, t`Failed to add ingredient`);
          if (message.length > 80) {
            const duration = Math.min(15000, Math.max(5000, message.length * 45));
            toast.error(message, { duration });
          } else {
            toast.error(message);
          }
        },
      },
    );
  }

  return (
    <IngredientDialogShell
      open={open}
      onClose={handleClose}
      title={t`Add ingredient`}
      recipeId={recipeId}
      form={form}
      setForm={setForm}
      autoFocusSearch
      isSaving={addIngredient.isPending}
      onSave={handleSave}
    />
  );
}

// ── EditIngredientDialog ───────────────────────────────────────────────────────

interface EditIngredientDialogProps {
  recipeId: number;
  ingredient: RecipeIngredient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The form as it opens on an existing line, whichever kind of line it is. */
function formFor(ingredient: RecipeIngredient): IngredientFormState {
  return {
    source: ingredient.subRecipeId !== null ? "RECIPE" : "MATERIAL",
    search: ingredientLineName(ingredient),
    selectedMaterial: (ingredient.material as unknown as Material) ?? null,
    selectedRecipe: ingredient.subRecipe ?? null,
    quantity: String(ingredient.quantity),
    unit: ingredient.unit,
    groupName: ingredient.groupName ?? "",
    prepNotes: ingredient.preparationNotes ?? "",
    isSubRecipe: false,
  };
}

export function EditIngredientDialog({
  recipeId,
  ingredient,
  open,
  onOpenChange,
}: EditIngredientDialogProps) {
  const { t } = useLingui();
  const updateIngredient = useUpdateIngredient();

  const [form, setForm] = useState<IngredientFormState>(() => ({
    ...formFor(ingredient),
  }));

  const [prevOpen, setPrevOpen] = useState(open);
  const [prevIngredientId, setPrevIngredientId] = useState(ingredient.id);
  if (prevOpen !== open || prevIngredientId !== ingredient.id) {
    setPrevOpen(open);
    setPrevIngredientId(ingredient.id);
    if (open) {
      setForm(formFor(ingredient));
    }
  }

  function handleClose() {
    onOpenChange(false);
  }

  function handleSave() {
    const qty = parseQuantityInput(form.quantity);
    if (!form.quantity || !Number.isFinite(qty) || qty <= 0) {
      toast.error(t`Please enter a valid quantity`);
      return;
    }
    if (!form.unit.trim()) {
      toast.error(t`Please select a unit of measure`);
      return;
    }

    updateIngredient.mutate(
      {
        recipeId,
        ingredientId: ingredient.id,
        data: {
          quantity: qty,
          unit: form.unit,
          preparationNotes: form.prepNotes.trim(),
          groupName: form.groupName.trim(),
        },
      },
      {
        onSuccess: () => {
          toast.success(t`Ingredient updated`);
          handleClose();
        },
        onError: () => toast.error(t`Failed to update ingredient`),
      },
    );
  }

  return (
    <IngredientDialogShell
      open={open}
      onClose={handleClose}
      title={t`Edit ingredient`}
      recipeId={recipeId}
      form={form}
      setForm={setForm}
      sourceLocked
      isSaving={updateIngredient.isPending}
      onSave={handleSave}
    />
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function FormField({
  label,
  required,
  optional,
  children,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  children: ReactNode;
}) {
  const { t } = useLingui();
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-(--color-text-primary)">
        {label}
        {required && <span className="ml-0.5 text-(--color-danger-text)">*</span>}
        {optional && (
          <span className="ml-1 font-normal text-(--color-text-secondary)">{t`(optional)`}</span>
        )}
      </label>
      {children}
    </div>
  );
}

function SectionRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-stretch border-b border-(--color-border)">
      <div className="w-[300px] shrink-0 bg-(--color-surface-muted) px-5 py-5">
        <p className="text-[26px] font-medium leading-none text-(--color-text-primary)">{title}</p>
        <p className="mt-2 text-sm leading-[1.2] text-(--color-text-secondary)">{description}</p>
      </div>
      <div className="flex-1 px-5 py-5">{children}</div>
    </div>
  );
}

/** Accepts decimals (`0.33`), fractions (`1/3`), and mixed numbers (`1 1/2`). */
function parseQuantityInput(raw: string): number {
  const value = raw.trim();
  if (!value) {
    return Number.NaN;
  }

  const mixed = value.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const numerator = Number(mixed[2]);
    const denominator = Number(mixed[3]);
    if (!Number.isFinite(whole) || !Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
      return Number.NaN;
    }
    return whole + numerator / denominator;
  }

  const fraction = value.match(/^(\d+)\/(\d+)$/);
  if (fraction) {
    const numerator = Number(fraction[1]);
    const denominator = Number(fraction[2]);
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
      return Number.NaN;
    }
    return numerator / denominator;
  }

  // "Infinity" and "1e309" are numbers to JavaScript, not quantities.
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}
